import {
    AccountInformation,
    ApiAccessLevel,
    ApiKeyInfo,
    CreateApiKeyRequest,
    FeatureType
} from '../../../types';
import { LitElement, css, html } from 'lit';
import { createOrganizationApiKey, deleteOrganizationApiKey, listOrganizationApiKeys } from '../../../helpers/api/organization-api';
import { customElement, query, state } from 'lit/decorators';

import { ErrorToastElement } from '../../error-toast';
import { StateController } from '../../../helpers/state/state-management/state-controller';
import accountInfo from '../../../helpers/state/account-info';
import featuresInfo from '../../../helpers/state/features-info';
import { getMainPage } from '../../../helpers/page-name';
import { openChatWindow } from '../../../helpers/intercom';
import organizationInfo from '../../../helpers/state/organization-info';
import topicData from '../../../helpers/state/topic-data';

@customElement('admin-integration-automation-addon')
export class AdminIntegrationAutomationAddon extends LitElement {
    amplitude_callsite: string = 'Integration Automation Addon';

    @query('#errorToast')
    private readonly errorToastElement?: ErrorToastElement;

    @state()
    selected_tab: string = 'About';

    @state()
    api_keys: ApiKeyInfo[] = [];

    @state()
    isLoadingApiKeys: boolean = false;

    @state()
    isCreatingApiKey: boolean = false;

    @state()
    newApiKeySecret: string | null = null;

    @state()
    newApiKeyAccessLevel: ApiAccessLevel = ApiAccessLevel.Read;

    options: string[] = ['About', 'API Keys', 'Webhooks', 'Zapier (Deprecated)', 'Additional Services'];

    private readonly bindTopicState = new StateController(this, topicData);
    private readonly bindAccountState = new StateController(this, accountInfo);
    private readonly bindOrganizationState = new StateController(this, organizationInfo);

    openUpgradeDrawer() {
        this.dispatchEvent(
            new CustomEvent('open-upgrade-dialog', {
                detail: {
                    featureType: FeatureType.ApiAccess
                }
            })
        );
    }

    handleChangeTab(e: CustomEvent) {
        const tabIndex = e.detail.tabIndex;
        this.selected_tab = this.options[tabIndex];

        // Load API keys when switching to API Keys tab
        if (this.selected_tab === 'API Keys') {
            void this.loadApiKeys();
        }
    }

    async loadApiKeys() {
        if (!topicData.topic?.organization_id) {
            return;
        }

        this.isLoadingApiKeys = true;
        try {
            this.api_keys = await listOrganizationApiKeys(
                this.amplitude_callsite,
                topicData.topic.organization_id.$oid
            );
        } catch (error) {
            console.error('Failed to load API keys:', error);
            getMainPage().errorToastElement?.showToast('Failed to load API keys');
        } finally {
            this.isLoadingApiKeys = false;
        }
    }

    async createApiKey() {
        if (!topicData.topic?.organization_id) {
            return;
        }

        if(!featuresInfo.apiAccessEnabled) {
            this.openUpgradeDrawer();
            return;
        }

        this.isCreatingApiKey = true;
        this.newApiKeySecret = null;

        try {
            const request: CreateApiKeyRequest = {
                access_level: this.newApiKeyAccessLevel
            };

            const result: AccountInformation = await createOrganizationApiKey(
                this.amplitude_callsite,
                topicData.topic.organization_id.$oid,
                request
            );

            // Store the secret to display temporarily
            this.newApiKeySecret = btoa(`${result.email}:${result.device_name}:${result.secret}`);

            // Reload the API keys list
            void this.loadApiKeys();
        } catch (error) {
            console.error('Failed to create API key:', error);
            getMainPage().errorToastElement?.showToast('Failed to create API key');
        } finally {
            this.isCreatingApiKey = false;
        }
    }

    async deleteApiKey(apiKeyId: string) {
        if (!topicData.topic?.organization_id) {
            return;
        }

        try {
            await deleteOrganizationApiKey(
                this.amplitude_callsite,
                topicData.topic.organization_id.$oid,
                apiKeyId
            );

            // Reload the API keys list
            void this.loadApiKeys();
        } catch (error) {
            console.error('Failed to delete API key:', error);
            getMainPage().errorToastElement?.showToast('Failed to delete API key');
        }
    }

    async copyToClipboard(text: string) {
        await getMainPage().errorToastElement.tryOrErrorToast(async () => {
            await navigator.clipboard.writeText(text);
            getMainPage().errorToastElement.showToast('Copied to clipboard');
        }, 'Failed to copy to clipboard');
    }

    dismissNewApiKey() {
        this.newApiKeySecret = null;
    }

    render() {
        if (!topicData.isReady) {
            return html`<loading-element loadingString="Getting integration info..."></loading-element>`;
        }
        return html`
            <div class="wrapper">
                <div class="header-title-and-buttons" style="background-color: ${topicData.backgroundColor}; color: ${topicData.textColor}">
                    <div class="left-header-content">
                        <form-button
                            size="md"
                            variant="text"
                            icon="arrow_back"
                            @click=${() => this.dispatchEvent(new CustomEvent('close-fragment'))}
                        >Back</form-button>
                    </div>
                    <div class="middle-header-content">
                        Integration and Automation Features
                        <br />
                        Map/Topic ID: ${topicData.topicId} <br /> Organization ID: ${topicData.topic?.organization_id?.$oid ?? 'N/A'}
                    </div>

                    <div class="right-header-content">
                    </div>
                </div>
                <div class="tabs">
                    <tabs-component
                        .options=${this.options}
                        .tabIndex=${this.options.indexOf(this.selected_tab)}
                        @on-select-tab=${this.handleChangeTab}
                    ></tabs-component>
                </div>
                <div class="tab__content">
                    ${this.selected_tab === 'About' ? this.renderAbout() : ''}
                    ${this.selected_tab === 'API Keys' ? this.renderApiKeys() : ''}
                    ${this.selected_tab === 'Webhooks' ? this.renderWebhooks() : ''}
                    ${this.selected_tab === 'Zapier (Deprecated)' ? this.renderZapier() : ''}
                    ${this.selected_tab === 'Additional Services' ? this.renderAdditionalServices() : ''}
                </div>
            </div>
                        <mwc-dialog
                id="electionModal"
                class="dialog"
                hideActions
                scrimClickAction=""
                escapeKeyAction=""
            >
        `;
    }

    renderAbout() {
        return html`
            <settings-card header="External APIs and Webhooks">
                <div class="feature-intro">
                    <p>Connect Proxi with your favorite tools and automate your workflow. Our APIs and webhooks make it easy to integrate Proxi into your existing systems.</p>
                </div>

                <div class="capabilities-grid">
                    <div class="capability-section">
                        <div class="capability-header">
                            <mwc-icon>webhook</mwc-icon>
                            <h3>Real-time Webhooks</h3>
                        </div>
                        <p class="capability-description">Get instant notifications when things happen on your map:</p>
                        <ul class="feature-list">
                            <li>Point submissions, updates, and deletions</li>
                            <li>Challenge registrations and completions</li>
                            <li>User check-ins and activity tracking</li>
                            <li>Award notifications</li>
                        </ul>
                    </div>

                    <div class="capability-section">
                        <div class="capability-header">
                            <mwc-icon>api</mwc-icon>
                            <h3>REST API Endpoints</h3>
                        </div>
                        <p class="capability-description">Programmatically manage your map content:</p>
                        <ul class="feature-list">
                            <li>Create, update, and delete points</li>
                            <li>Register users for challenges</li>
                            <li>Track user progress and check-ins</li>
                            <li>Retrieve location data</li>
                        </ul>
                    </div>
                </div>

                <div class="getting-started">
                    <div class="cta-section">
                        <h4>Ready to get started?</h4>
                        <div class="cta-buttons">
                            <form-button
                                variant="outlined"
                                @click="${() => window.open('https://proximaps.notion.site/apidocs', '_blank')}"
                            >
                                <mwc-icon slot="icon">description</mwc-icon>
                                View API Docs
                            </form-button>
                            <form-button
                                variant="filled"
                                @click="${() => openChatWindow(this.amplitude_callsite, 'I need help setting up API integration')}"
                            >
                                <mwc-icon slot="icon">support_agent</mwc-icon>
                                Get Help
                            </form-button>
                        </div>
                    </div>
                </div>
            </settings-card>
        `;
    }

    renderApiKeys() {
        if (!organizationInfo.isReady || !organizationInfo.organization) {
            return html`<loading-element loadingString="Loading organization info..."></loading-element>`;
        }

        return html`
            <settings-card header="API Keys (beta)">
                <div class="api-keys-section">
                    <div class="feature-intro">
                        <p>Create and manage API keys for your organization. API keys allow you to access the Proxi API programmatically.</p>
                    </div>
                    ${organizationInfo.organization.is_administrator ? html`
                        ${this.newApiKeySecret ? this.renderNewApiKeySecret() : ''}

                        <div class="create-api-key-section">
                            <h4>Create New API Key</h4>
                            <div class="create-form">
                                <div class="form-field">
                                    <label for="access-level">Access Level:</label>
                                    <select
                                        id="access-level"
                                        .value=${this.newApiKeyAccessLevel}
                                        @change=${(e: Event) => {
                                            this.newApiKeyAccessLevel = (e.target as HTMLSelectElement).value as ApiAccessLevel;
                                        }}
                                        ?disabled=${this.isCreatingApiKey}
                                    >
                                        <option value=${ApiAccessLevel.Read}>Read Only</option>
                                        <option value=${ApiAccessLevel.Write}>Read & Write</option>
                                    </select>
                                </div>
                                <form-button
                                    variant="filled"
                                    ?disabled=${this.isCreatingApiKey}
                                    @click=${this.createApiKey}
                                >
                                    ${this.isCreatingApiKey ? 'Creating...' : 'Create API Key'}
                                </form-button>
                            </div>
                        </div>
                    `:  ''}
                    <div class="existing-api-keys-section">
                        <h4>Existing API Keys</h4>
                        ${this.isLoadingApiKeys ?
                            html`<loading-element loadingString="Loading API keys..."></loading-element>` :
                            this.renderApiKeysList()
                        }
                    </div>
                </div>
            </settings-card>
        `;
    }

    renderNewApiKeySecret() {
        return html`
            <div class="new-api-key-display">
                <div class="api-key-secret-header">
                    <mwc-icon>warning</mwc-icon>
                    <h4>New API Key Created</h4>
                </div>
                <p>Please copy and save this API key. You won't be able to see it again!</p>
                <div class="secret-display">
                    <code>${this.newApiKeySecret}</code>
                    <form-button
                        variant="outlined"
                        size="sm"
                        @click=${() => this.newApiKeySecret && this.copyToClipboard(this.newApiKeySecret)}
                    >
                        <mwc-icon slot="icon">content_copy</mwc-icon>
                        Copy
                    </form-button>
                </div>
                <form-button
                    variant="text"
                    @click=${this.dismissNewApiKey}
                >
                    I've saved the key
                </form-button>
            </div>
        `;
    }

    renderApiKeysList() {
        if (!(organizationInfo.organization?.is_administrator ?? false)) {
            return html`
                <div class="empty-state">
                    <p>You do not have permission to view API keys.</p>
                </div>
            `;
        }
        if (this.api_keys.length === 0) {
            return html`
                <div class="empty-state">
                    <p>No API keys found. Create your first API key above.</p>
                </div>
            `;
        }

        return html`
            <div class="api-keys-list">
                ${this.api_keys.map(apiKey => this.renderApiKeyItem(apiKey))}
            </div>
        `;
    }

    renderApiKeyItem(apiKey: ApiKeyInfo) {
        return html`
            <div class="api-key-item">
                <div class="api-key-info">
                    <div class="api-key-header">
                        <span class="api-key-id">ID: ${apiKey.id.$oid}</span>
                        <span class="api-key-access-level ${apiKey.access_level}">
                            ${apiKey.access_level === ApiAccessLevel.Read ? 'Read Only' : 'Read & Write'}
                        </span>
                    </div>
                    <div class="api-key-dates">
                        <span>Created: ${new Date(apiKey.created_at).toLocaleDateString()}</span>
                        <span>Expires: ${new Date(apiKey.expires_at).toLocaleDateString()}</span>
                    </div>
                </div>
                <div class="api-key-actions">
                    <form-button
                        variant="outlined"
                        size="sm"
                        @click=${async () => await this.deleteApiKey(apiKey.id.$oid)}
                    >
                        <mwc-icon slot="icon">delete</mwc-icon>
                        Delete
                    </form-button>
                </div>
            </div>
        `;
    }

    renderWebhooks() {
        if (!(organizationInfo.organization?.is_administrator ?? false)) {
            return html`
                <div class="empty-state">
                    <p>You do not have permission to view webhooks.</p>
                </div>
            `;
        }
        return html`
            <webhook-management-component @open-upgrade-dialog=${this.openUpgradeDrawer}></webhook-management-component>
        `;
    }

    renderZapier() {
        return html`
            <settings-card header="Zapier (Deprecated)">
                <admin-zapier-integration-addon></admin-zapier-integration-addon>
            </settings-card>
        `;
    }

    renderAdditionalServices() {
        return html`
            <settings-card header="Integration Done For You">
                <p>
                    We're here to help you with your integration needs. If you require assistance or a custom integration solution, please reach out to help@proxi.co or start a chat
                    <span @click="${() => openChatWindow(this.amplitude_callsite, 'I am looking for integration assistance')}" class="helpSubHeader" style="cursor: pointer; color: var(--theme-primary-color);text-decoration: underline;">
                        start a chat.
                    </span>
                </p>
            </settings-card>
        `;
    }

    static get styles() {
        return css`

            .wrapper {
                display: flex;
                flex-direction: column;
                overflow: hidden auto;
                box-sizing: border-box;
                width: 100%;
                height: 100%
            }

            .header-title-and-buttons {
                display: flex;
                flex-direction: row;
                align-items: center;
                justify-content: space-between;
                padding: 10px;
                font-size: 18px;
                background-color: var(--theme-border-color);
                border-bottom: 1px solid var(--theme-border-color);
            }

            .left-header-content {
                display: flex;
                flex-direction: row;
                align-items:center;
                font-size: 0.8rem;
                gap: 5px;
                flex: 1;
                flex-wrap: wrap;
            }

            .checkboxHolder {
                display: flex;
                flex-direction: row;
                align-items: center;
            }

            .right-header-content {
                display: flex;
                flex-direction: row;
                align-items:center;
                font-size: 0.8rem;
                gap:5px;
                justify-content: flex-end;
                flex: 1;
                flex-wrap: wrap;
            }
            .modalContent {
                position: relative;
                width: 100%;
                height: auto;
                overflow-y: auto;
                box-sizing: border-box;
                height: 100%;
                display: flex;
                flex-direction: column;
            }

            .middle-header-content {
                flex: 1;
                font-size: 1rem;
                text-align:center;
            }

            .header-title-and-buttons h4 {
                margin: 5px 0;
            }

            .tabs {
                display: flex;
                align-items: center;
                justify-content: center;
                padding: 0 20px;
                background-color: var(--theme-white-color);
                border-bottom: 1px solid var(--theme-border-color);
                box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.1);
            }

            .tab__content {
                padding: 20px;
                display: flex;
                gap: 20px;
                flex: 1;
                overflow: hidden auto;
                justify-content: flex-start;
                align-items: stretch;
                background-color: var(--theme-border-color);
                flex-direction: column;
            }

            .sub-title {
                margin-bottom: 0px;
                color: var(--theme-input-color);
                display: inline-flex;
                --mdc-icon-size: 16px;
            }

            p {
                margin-top: 5px;
                margin-bottom: 5px;
            }

            .general-stats-area {
                display: flex;
                flex-direction: column;
                gap: 10px;
                flex: 1;
            }

            h4.sub-title {
                font-weight: 600;
                font-size: 12px;
                line-height: 100%;
                color: #000000;
                text-align: left;
                margin: 15px 0 5px;
                display: flex;
                align-items: center;
                flex-direction: row;
            }

            /* width */
            .tab__content::-webkit-scrollbar {
                width: 5px;
            }

            /* Track */
            .tab__content::-webkit-scrollbar-track {
                background: var(--theme-light-scrollbar-color);
            }

            /* Handle */
            .tab__content::-webkit-scrollbar-thumb {
                background: var(--main-moon-color);
            }

            #holder {
                display: flex;
                flex-direction: column;
                height: 100%;
            }

            #container {
                flex-direction: column;
                overflow: hidden auto;
                padding-right: 20px;
                box-sizing: border-box;
                flex: 1;
            }

            /* width */
            #container::-webkit-scrollbar {
                width: 5px;
            }

            /* Track */
            #container::-webkit-scrollbar-track {
                background: var(--theme-light-scrollbar-color);
            }

            /* Handle */
            #container::-webkit-scrollbar-thumb {
                background: var(--main-moon-color);
            }

            .title {
                color: var(--main-primary-color);
                text-align: center;
                font-size: 18px;
                font-weight: 700;
                line-height: 130%;
                margin: 5px 0;
                text-align: left;
            }

            .info-paragraph {
                color: var(--theme-input-color);
                margin-top: 0;
                margin-bottom: 0;
            }

            .sub-title {
                color: var(--theme-input-color);
                display: inline-flex;
                --mdc-icon-size: 16px;
                margin-top: 5px;
            }

            .save {
                --mdc-theme-primary: var(--main-success-color);
            }

            .green {
                --mdc-theme-primary: var(--main-success-color);
            }

            .gray {
                --mdc-theme-primary: var(--main-disabled-color);
            }

            .save-actions {
                display: flex;
                justify-content: flex-end;
                margin: 0.5rem;
            }

            .visibility-square {
                display: flex;
                margin-left: 0.5rem;
                color: var(--theme-input-color);
                border: 1px solid var(--theme-input-color);
                border-radius: 5px;
                padding: 0.1rem 0.25rem;
                align-items: center;
                font-size: 0.8rem;
            }

            .manage-participants-and-results {
                display: flex;
                flex-direction: row;
                gap: 10px;
                height: 100%;
            }

            .individual-participant-drawer {
                border-right: 1px solid var(--theme-border-color);
                box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.1);
                background-color: var(--theme-white-color);
                padding: 1rem;
                transition: width 0.3s;
                display: flex;
                flex-direction: column;
                display: flex;
                overflow-y: auto;
            }

            .individual-participant-drawer.open {
                width: 30%;
            }

            .individual-participant-drawer.closed {
                width: 0;
                padding: 0;
            }

            .helpSubHeader {
                display: inline-flex;
                margin-bottom: 10px;
                font-size: 0.75rem;
                color: var(--main-primary-color);
                align-items: center;
            }

            .summary {
                display: flex;
                align-items: center;
                justify-content: space-between;
            }

            .summary-section {
                display: flex;
                flex-direction: row;
                align-items: center;
            }

            .disabled {
                background-color: var(--theme-border-color);
                color: var(--theme-input-color);
                pointer-events: none;
            }

            .move {
                margin-left: 1rem;
            }

            .summary mwc-icon {
                margin-right: 0.5rem;
                --mdc-icon-size: 20px;
            }

            .move-icon {
                cursor: pointer;
                margin: 0;
            }

            .move-icon:hover {
                color: var(--main-primary-color);
            }

            .existingRules {
                display: flex;
                flex-direction: column;
                --mdc-icon-size: 16px;
            }

            .existingRules mwc-icon {
                cursor: pointer;
                margin-right: 0.5rem;
                margin-left: 0.5rem;
            }

            .ruleName {
                margin: 0;
                font-size: 0.8rem;
                text-align: center;
                flex: 1;
                border: 1px solid var(--main-secondary-color);
                display: flex;
                align-items: center;
                gap: 5px;
            }

            .rule {
                display: flex;
                flex-direction: column;
                align-items: center;
                border: 1px solid var(--main-primary-color);
                padding: 0.5rem;
            }

            .operator-holder {
                display: flex;
                justify-content: center;
                flex-direction:row;

            }

            .operator {
                text-align: center;
                font-size: 0.6rem;
                font-weight: bold;
                margin-top: 0.25rem;
                margin-bottom: 0.25rem;
                color: white;
                border-radius: 5px;
                padding: 0 0.5rem;
                --mdc-icon-size: 12px;
            }

            .operator mwc-icon {
                margin-left: 0;
                margin-right: 0.25rem;

            }

            .and {
                background-color: var(--main-primary-color);
                color : white;
            }

            .or {
                background-color: var(--main-secondary-color);
                color: white;
            }

            .clickable-or {
                cursor: pointer;
                color: var(--main-secondary-color);
                background-color: white;
                display: inline-flex;
                align-items: center;
            }

            .clickable-or:hover {
                background-color: var(--main-secondary-color);
                color: white;
            }

            .clickable-and {
                cursor: pointer;
                color: var(--main-primary-color);
                background-color: white;
                display: inline-flex;
                align-items: center;
            }

            .clickable-and:hover {
                background-color: var(--main-primary-color);
                color: white;
            }

            .visibilityRules {
                display: flex;
                flex-direction: column;
                margin-top: 1rem;
            }

            .helpSubeHeaderUnderline {
                margin: 0px;
                font-weight: normal;
                font-size: 0.8rem;
                flex: 1;
                text-decoration: underline;
                color: var(--theme-primary-color);
                display: inline-flex;
            }

            form-tooltip {
                font-size: 0.8rem;
            }

            .archiveField {
                cursor: pointer;
                display: flex;
                align-items: flex-end;
                justify-content: flex-end;
                text-decoration: underline;
                color: var(--main-invalid-color);
            }

            .helpHeader {
                color: var(--main-primary-color);
            }

            .input {
                width: 100%;
                padding: auto 5px;
                box-sizing: border-box;
            }

            .checkbox-wrapper {
                width: 100%;
                display: flex;
                align-items: center;
                justify-content: flex-start;
                gap: 8px;
                line-height: 120%;
                color: #102C3D;
                margin-top: 15px;
            }

            .ruleSelectorWrapper {
                display: flex;
                margin-top: 1rem;
                flex-direction: row;
                flex-wrap: wrap;
                align-content: center;
                justify-content: center;
                gap: 3px 5px;
            }

            .gatedSection {
                cursor: pointer;
            }

            .description {
                color: var(--theme-input-color);
            }

            .add-new-rule-wrapper {
                width: 100%;
                display: flex;
                justify-content: center;
                padding-top: 5px;
            }

            .customizedFields__disabled {
                opacity: 0.5;
                touch-action: none;
                pointer-events: none;
            }

            .hidden {
                display: none;
            }

            .ruleSubmissionButtons {
                display: flex;
                justify-content: center;
                margin-top: 5px;
                padding-bottom: 10px;
                gap: 5px;
            }

            .feature-intro {
                padding: 16px;
                background-color: var(--theme-white-color);
                border-radius: 8px;
            }

            .feature-intro p {
                margin: 0;
                font-size: 1rem;
                line-height: 1.5;
                color: var(--theme-input-color);
            }

            .capabilities-grid {
                display: grid;
                grid-template-columns: 1fr 1fr;
                gap: 20px;
                margin-bottom: 24px;
            }

            @media (max-width: 768px) {
                .capabilities-grid {
                    grid-template-columns: 1fr;
                }
            }

            .capability-section {
                background-color: var(--theme-white-color);
                border-radius: 8px;
                padding: 20px;
                border: 1px solid var(--theme-border-color);
            }

            .capability-header {
                display: flex;
                align-items: center;
                gap: 12px;
                margin-bottom: 12px;
            }

            .capability-header mwc-icon {
                --mdc-icon-size: 24px;
                color: var(--main-primary-color);
            }

            .capability-header h3 {
                margin: 0;
                font-size: 1.1rem;
                font-weight: 600;
                color: var(--main-primary-color);
            }

            .capability-description {
                margin: 0 0 12px 0;
                color: var(--theme-input-color);
                font-size: 0.9rem;
            }

            .feature-list {
                margin: 0;
                padding-left: 20px;
            }

            .feature-list li {
                margin-bottom: 8px;
                color: var(--theme-input-color);
                font-size: 0.9rem;
                line-height: 1.4;
            }

            .getting-started {
                background-color: var(--theme-white-color);
                border-radius: 8px;
                padding: 20px;
                text-align: center;
                border: 1px solid var(--theme-border-color);
            }

            .cta-section h4 {
                margin: 0 0 16px 0;
                color: var(--main-primary-color);
                font-size: 1.1rem;
            }

            .cta-buttons {
                display: flex;
                gap: 12px;
                justify-content: center;
                flex-wrap: wrap;
            }

            @media (max-width: 480px) {
                .cta-buttons {
                    flex-direction: column;
                    align-items: center;
                }

                .cta-buttons form-button {
                    width: 100%;
                    max-width: 200px;
                }

                .helpHeader {
                    margin: 0.2em 0;
                }
            }

            /* API Keys specific styles */
            .api-keys-section {
                display: flex;
                flex-direction: column;
                gap: 24px;
            }

            .create-api-key-section {
                background-color: var(--theme-white-color);
                border-radius: 8px;
                padding: 20px;
                border: 1px solid var(--theme-border-color);
            }

            .create-api-key-section h4 {
                margin: 0 0 16px 0;
                color: var(--main-primary-color);
                font-size: 1.1rem;
            }

            .create-form {
                display: flex;
                flex-direction: column;
                gap: 16px;
            }

            .form-field {
                display: flex;
                flex-direction: column;
                gap: 8px;
            }

            .form-field label {
                font-weight: 600;
                color: var(--theme-input-color);
                font-size: 0.9rem;
            }

            .form-field select {
                padding: 8px 12px;
                border: 1px solid var(--theme-border-color);
                border-radius: 4px;
                font-size: 0.9rem;
                background-color: var(--theme-white-color);
                color: var(--theme-input-color);
            }

            .new-api-key-display {
                background-color: #fff3cd;
                border: 1px solid #ffeaa7;
                border-radius: 8px;
                padding: 20px;
                margin-bottom: 20px;
            }

            .api-key-secret-header {
                display: flex;
                align-items: center;
                gap: 8px;
                margin-bottom: 12px;
            }

            .api-key-secret-header mwc-icon {
                color: #f39c12;
                --mdc-icon-size: 20px;
            }

            .api-key-secret-header h4 {
                margin: 0;
                color: #e67e22;
                font-size: 1.1rem;
            }

            .secret-display {
                display: flex;
                align-items: center;
                gap: 12px;
                margin: 16px 0;
                padding: 12px;
                background-color: var(--theme-white-color);
                border: 1px solid var(--theme-border-color);
                border-radius: 4px;
            }

            .secret-display code {
                flex: 1;
                font-family: 'Courier New', monospace;
                background-color: transparent;
                padding: 0;
                font-size: 0.9rem;
                word-break: break-all;
            }

            .existing-api-keys-section h4 {
                margin: 0 0 16px 0;
                color: var(--main-primary-color);
                font-size: 1.1rem;
            }

            .api-keys-list {
                display: flex;
                flex-direction: column;
                gap: 12px;
            }

            .api-key-item {
                display: flex;
                justify-content: space-between;
                align-items: center;
                padding: 16px;
                background-color: var(--theme-white-color);
                border: 1px solid var(--theme-border-color);
                border-radius: 8px;
            }

            .api-key-info {
                display: flex;
                flex-direction: column;
                gap: 8px;
            }

            .api-key-header {
                display: flex;
                align-items: center;
                gap: 12px;
            }

            .api-key-id {
                font-family: 'Courier New', monospace;
                font-size: 0.85rem;
                color: var(--theme-input-color);
            }

            .api-key-access-level {
                padding: 4px 8px;
                border-radius: 4px;
                font-size: 0.8rem;
                font-weight: 600;
            }

            .api-key-access-level.read {
                background-color: #e8f4fd;
                color: #1976d2;
            }

            .api-key-access-level.write {
                background-color: #fff3e0;
                color: #f57c00;
            }

            .api-key-dates {
                display: flex;
                gap: 16px;
                font-size: 0.85rem;
                color: var(--theme-input-color);
            }

            .empty-state {
                text-align: center;
                padding: 40px;
                color: var(--theme-input-color);
            }

            .empty-state p {
                margin: 0;
                font-style: italic;
            }

            @media (max-width: 768px) {
                .api-key-item {
                    flex-direction: column;
                    align-items: flex-start;
                    gap: 12px;
                }

                .api-key-dates {
                    flex-direction: column;
                    gap: 4px;
                }

                .secret-display {
                    flex-direction: column;
                    align-items: stretch;
                }
            }
        `;
    }
}
